import { Dimension, Skill } from '../types';
import { habilidadesComunicacionalesDimension } from '../components/HabilidadesComunicacionales';
import { lenguajesArtisticosDimension } from '../components/LenguajeArtistico';

/**
 * Determina si un curso es anterior a Pre Kinder (Medio Mayor)
 */
export const isBeforePreKinder = (course: string): boolean => {
  return course.includes('Medio Mayor');
};

/**
 * Determina si un curso está en el rango de Pre Kinder a Kinder (incluidos)
 */
export const isPreKinderToKinder = (course: string): boolean => {
  return course.includes('Pre Kinder') || course.includes('Kinder');
};

/**
 * Determina si un curso es posterior a Kinder (Primero básico en adelante)
 */
export const isAfterKinder = (course: string): boolean => {
  return course.includes('básico');
};

/**
 * Modifica la dimensión de Lenguaje Artístico según el curso
 * Hasta Kinder: Lenguaje Artístico conjunto
 * Después de Kinder: Lenguaje Visual y Musical separados
 */
export const adjustLenguajeArtisticoDimensions = (dimensions: Dimension[], course: string): Dimension[] => {
  const artisticDimensionIndex = dimensions.findIndex(dim => dim.id === 5);
  if (artisticDimensionIndex === -1) return dimensions;

  const artisticDimension = dimensions[artisticDimensionIndex];
  
  if (isPreKinderToKinder(course)) {
    // Para Pre Kinder y Kinder: mantener como "Lenguaje Artístico" conjunto
    return dimensions.map(dim => 
      dim.id === 5 
        ? { ...dim, name: "Lenguaje Artístico" }
        : dim
    );
  } else {
    // Para cursos posteriores a Kinder: separar en Visual y Musical
    const result = [...dimensions];
    result.splice(artisticDimensionIndex, 1); // Quitar la dimensión original

    // Agregar Lenguaje Visual
    const lenguajeVisualDimension: Dimension = {
      id: 5,
      name: "Lenguaje Visual",
      skills: artisticDimension.subDimensions?.find(sub => sub.name === "Lenguaje Visual")?.skills || []
    };

    // Agregar Lenguaje Musical
    const lenguajeMusicialDimension: Dimension = {
      id: 13, // Usar un ID diferente para evitar conflictos
      name: "Lenguaje Musical",  
      skills: artisticDimension.subDimensions?.find(sub => sub.name === "Lenguaje Musical")?.skills || []
    };

    // Insertar en la misma posición
    result.splice(artisticDimensionIndex, 0, lenguajeVisualDimension, lenguajeMusicialDimension);
    
    return result;
  }
};

/**
 * Determina si una skill específica debe mostrarse en el PDF según el curso
 */
export const shouldShowSkillInPDF = (skillId: number, dimensionId: number, course: string): boolean => {
  // Para la dimensión de Habilidades comunicacionales (ID 3)
  // Las skills de escritura (IDs 34-76) solo se muestran desde Pre Kinder en adelante
  if (dimensionId === 3 && skillId >= 34 && skillId <= 76) {
    return !isBeforePreKinder(course); // Solo mostrar si NO es Medio Mayor
  }
  
  // Para todas las demás skills, siempre mostrar
  return true;
};

/**
 * Filtra las skills de una dimensión según el curso
 */
export const filterSkillsByCourse = (dimension: Dimension, course: string): Skill[] => {
  return dimension.skills.filter(skill => 
    shouldShowSkillInPDF(skill.id, dimension.id, course)
  );
};

/**
 * Función principal para filtrar dimensiones según el curso del estudiante
 */
export const filterDimensionsByCourse = (dimensions: Dimension[], course: string): Dimension[] => {
  return dimensions.map(dimension => ({
    ...dimension,
    skills: filterSkillsByCourse(dimension, course)
  }));
};

/**
 * Función para obtener el nombre de dimensión ajustado según el curso
 */
export const getDimensionNameByCourse = (dimensionId: number, dimensionName: string, course: string): string => {
  // Para Lenguaje Artístico, ajustar el nombre según el curso
  if (dimensionId === 5) {
    if (isPreKinderToKinder(course)) {
      return "Lenguaje Artístico";
    } else {
      // En cursos posteriores, esta sería la dimensión Visual
      return "Lenguaje Visual";
    }
  }
  
  if (dimensionId === 13) {
    return "Lenguaje Musical";
  }

  return dimensionName;
};
